<template>
  <div class="page" data-qa="page" ref="pageRef">
    <div class="page-header" data-qa="page-header" ref="pageHeaderRef">
      <div class="page-header-first-level">
        <div class="rbz-text-h3">
          <slot name="pageHeadline" />
        </div>
      </div>
      <layout-divider-line color="gray" class="page-header-divider" />
      <div
        class="page-header-second-level"
        v-if="
          $slots.secondLevelHeaderEntire ||
          $slots.secondLevelHeaderLeft ||
          $slots.secondLevelHeaderRight
        "
      >
        <div class="entire">
          <slot name="secondLevelHeaderEntire" />
        </div>
        <div class="left">
          <slot name="secondLevelHeaderLeft" />
        </div>
        <div class="right">
          <slot name="secondLevelHeaderRight" />
        </div>
      </div>
    </div>
    <div class="page-content" data-qa="page-content" ref="pageContentRef">
      <rbz-loader
        v-if="showLoader"
        :loading="!!isLoading"
        :data-exists="!!dataExists"
        height="100%"
        :style="{ 'align-items': 'start', 'padding-top': '150px' }"
      />
      <template v-if="!showLoader || dataExists">
        <div v-show="!isLoading">
          <slot
            name="pageContent"
            :page-header-ref="$refs.pageHeaderRef"
            :page-content-ref="$refs.pageContentRef"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayoutDividerLine from '../components/LayoutDividerLine.vue';
import RbzLoader from '../components/RbzLoader.vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

interface PageTemplate {
  showLoader?: boolean;
  isLoading?: boolean;
  dataExists?: boolean;
}

withDefaults(defineProps<PageTemplate>(), {
  showLoader: true,
});

const pageRef = ref(null);
const pageHeaderRef = ref(null);
const pageContentRef = ref(null);

const fixFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  if (!pageContentRef.value.contains(target) || target.classList.contains('ignore-fix-focus')) {
    return;
  }
  const headerBottomPosition = pageHeaderRef.value.getBoundingClientRect().bottom;
  const focusedElement = target.getBoundingClientRect();
  const focusedElementTop = focusedElement.top - 20;
  if (focusedElementTop < headerBottomPosition) {
    nextTick(() => {
      pageRef.value.scrollBy(0, focusedElementTop - headerBottomPosition);
    });
  }
};

onMounted(() => {
  document.addEventListener('focusin', fixFocus);
});

onBeforeUnmount(() => {
  document.removeEventListener('focusin', fixFocus);
});
</script>

<style scoped lang="scss">
@use '../../assets/styles/main';
@use '../../assets/styles/colors' as colors;
@use 'sass:map';

.page-header {
  background-color: map.get(colors.$colors, 'white');
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  position: static;
}

.page-header-first-level {
  height: 30px;
}

.page-header-second-level {
  .entire {
    width: 100%;
  }

  .left {
    align-items: center;
    display: flex;
    float: left;
    gap: 12px;
    position: relative;

    ::slotted(.vertical-divider) {
      height: 24px;
      position: relative;
    }

    :deep(.expand-icon-wrapper svg) {
      @extend .color-black;
    }

    :deep(.menu-list-item) {
      @extend .rbz-text-regular;

      color: map.get(colors.$colors, 'stratos');
      margin-bottom: 0;
    }
  }

  .right {
    display: flex;
    float: right;
    gap: 12px;
    position: relative;
  }
}

:deep(.switch-page-entry) {
  max-width: 50vw;
  width: fit-content;

  .dropdown-header {
    border-color: transparent;
    max-height: 36px;
    min-height: 36px;
    padding: 5px 11px;

    &.open {
      background: map.get(colors.$colors, 'selago');
      border-radius: 4px;
      box-shadow: none;
    }

    &:active {
      background: map.get(colors.$colors, 'link-water');
      border-color: map.get(colors.$colors, 'fun-blue');
    }

    &:focus:not(:active) {
      background: map.get(colors.$colors, 'white');
      border-color: map.get(colors.$colors, 'fun-blue');
      box-shadow: 0 0 0 2px map.get(colors.$colors, 'malibu');
      outline: none;
    }
  }

  .dropdown-placeholder {
    @extend .rbz-text-h4;

    color: map.get(colors.$colors, 'stratos');
    margin-bottom: 0;
  }

  .dropdown-list {
    border-top-width: 1px;
    margin-top: 4px;
    max-width: 50vw;
    min-width: 100%;
    width: fit-content;
  }
}

.page-content {
  background-color: map.get(colors.$colors, 'white');
  padding: 0 32px 32px;
  position: relative;
}
</style>
